/**
 * /invite/:token — public invitation acceptance page.
 *
 * The token in the URL is the bearer credential. POST to
 * /team/invites/{token}/accept creates a User row + Membership at the
 * invited role. After accept, we send the user to /dashboard (or to
 * /signin if they need to authenticate first).
 */
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CheckCircle2, Loader2, Sparkles } from 'lucide-react';
import toast from 'react-hot-toast';
import teamService from '../services/teamService';


const InviteAcceptPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [fullName, setFullName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  const handleAccept = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) {
      toast.error('Invitation link is missing the token.');
      return;
    }
    setSubmitting(true);
    const res = await teamService.acceptInvite(token, fullName.trim() || undefined);
    setSubmitting(false);
    if (res.error) {
      toast.error(res.error);
      return;
    }
    setAccepted(true);
    toast.success(`Welcome to ${res.data?.organization.name || 'the team'}`);
    setTimeout(() => navigate('/signin'), 1500);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50 flex items-center justify-center px-4 py-10">
      <div className="max-w-md w-full bg-white rounded-xl shadow-sm border border-gray-200 p-8">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-6 h-6 text-indigo-500" />
          <span className="font-semibold text-lg">VentureOS</span>
        </div>
        {accepted ? (
          <div className="text-center py-6">
            <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto" />
            <h1 className="text-xl font-semibold mt-3">You're in.</h1>
            <p className="text-gray-600 mt-2">
              Redirecting you to sign in…
            </p>
          </div>
        ) : (
          <>
            <h1 className="text-2xl font-semibold mt-2">Accept your invitation</h1>
            <p className="text-gray-600 mt-2 leading-relaxed">
              You've been invited to join a team on the VentureOS.
              Add your name (optional) and click accept — you'll then sign
              in with the email address that received this invitation.
            </p>
            <form onSubmit={handleAccept} className="mt-6 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Your name (optional)
                </label>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Jane Founder"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:bg-gray-300"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                Accept invitation
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default InviteAcceptPage;
